import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { CustomerLink, TechLink } from "@/components/links";
import {
  displayCustomerName,
  formatTime,
  resolveWindowAndPriority,
  formatDuration,
} from "@/lib/format";
import {
  STATUS_DONE,
  STATUS_ACTIVE,
  type Dispatch,
} from "@/lib/types";
import { windowColorClasses, useDispatchNote, useSaveDispatchNote } from "./api";
import { cn } from "@/lib/cn";

interface Props {
  dispatch: Dispatch | null;
  onClose: () => void;
}

/**
 * Modal wrapper used by the Columns / Map board views (click a card or pin →
 * pop the detail). The Cockpit view renders {@link DispatchDetailBody} inline
 * in its bottom strip (variant="strip"), so the detail content lives in one
 * place.
 */
export function DispatchDetailModal({ dispatch, onClose }: Props) {
  if (!dispatch)
    return <Modal open={false} onClose={onClose} children={null} />;

  const customer = displayCustomerName(
    dispatch.locname,
    dispatch.customer_fullname,
  );

  return (
    <Modal
      open={true}
      onClose={onClose}
      title={customer}
      subtitle={`Dispatch ${dispatch.dispatch}`}
      size="lg"
    >
      <DispatchDetailBody dispatch={dispatch} onClose={onClose} />
    </Modal>
  );
}

/**
 * The dispatch detail content, sans modal chrome.
 *
 * - `variant="modal"` (default): a vertical stack — used inside the modal.
 * - `variant="strip"`: a horizontal multi-column layout that fits the Cockpit
 *   view's bottom strip on one screen without page scroll (only the notes box
 *   scrolls if long).
 *
 * Pass `showHeader` when there's no surrounding title (the cockpit strip); the
 * modal supplies its own title.
 */
export function DispatchDetailBody({
  dispatch,
  onClose,
  showHeader = false,
  variant = "modal",
}: {
  dispatch: Dispatch;
  onClose: () => void;
  showHeader?: boolean;
  variant?: "modal" | "strip";
}) {
  const navigate = useNavigate();

  const customer = displayCustomerName(
    dispatch.locname,
    dispatch.customer_fullname,
  );
  const { timeWindow, priority } = resolveWindowAndPriority(
    dispatch.window_code,
    dispatch.notes,
  );
  const windowColors = timeWindow ? windowColorClasses(timeWindow) : null;
  const priorityColors = priority ? windowColorClasses(priority) : null;
  const time = formatTime(dispatch.tpromtime);
  const techName =
    dispatch.tech_name ??
    `${dispatch.tech_first ?? ""} ${dispatch.tech_last ?? ""}`.trim();
  const status = dispatch.status ?? "Pending";
  const isDone = STATUS_DONE.has(status);
  const isActive = STATUS_ACTIVE.has(status);

  // Pegasus-native editable note (additive layer; ESC's note stays read-only).
  const { data: pegNote } = useDispatchNote(dispatch.dispatch);
  const saveNote = useSaveDispatchNote();
  const savedNote = pegNote?.note ?? "";
  const [draft, setDraft] = useState(savedNote);
  useEffect(() => {
    setDraft(pegNote?.note ?? "");
  }, [dispatch.dispatch, pegNote?.note]);
  const dirty = draft !== savedNote;

  const headerEl = showHeader ? (
    <div>
      <h3 className="text-base font-semibold text-slate-900">{customer}</h3>
      <p className="text-xs text-slate-500">Dispatch {dispatch.dispatch}</p>
    </div>
  ) : null;

  const pillsEl = (
    <div className="flex flex-wrap items-center gap-2">
      <span
        className={cn(
          "rounded-full px-2.5 py-1 text-xs font-semibold",
          isDone && "bg-slate-100 text-slate-500",
          isActive && "bg-emerald-50 text-emerald-700",
          !isDone && !isActive && "bg-blue-50 text-blue-700",
        )}
      >
        {status}
      </span>
      {timeWindow && windowColors && (
        <span
          className={cn(
            "rounded-full px-2.5 py-1 text-xs font-semibold",
            windowColors.bg,
            windowColors.text,
          )}
        >
          Window: {timeWindow}
        </span>
      )}
      {priority && priorityColors && (
        <span
          className={cn(
            "rounded-full px-2.5 py-1 text-xs font-semibold",
            priorityColors.bg,
            priorityColors.text,
          )}
        >
          Priority: {priority}
        </span>
      )}
      {time && (
        <span className="rounded-full bg-slate-50 px-2.5 py-1 text-xs text-slate-700">
          {time}
        </span>
      )}
    </div>
  );

  const addressEl =
    dispatch.service_address || dispatch.city ? (
      <Section label="Service address">
        <div className="text-sm text-slate-900">
          {dispatch.service_address}
          {dispatch.city && (
            <>
              <br />
              {dispatch.city}, {dispatch.state} {dispatch.zip}
            </>
          )}
        </div>
      </Section>
    ) : null;

  const techEl = (
    <Section label="Assigned tech">
      <TechLink empno={dispatch.tech_empno} name={techName || "—"} />
    </Section>
  );

  const phoneEl = dispatch.customer_phone ? (
    <Section label="Customer phone">
      <a
        href={`tel:${dispatch.customer_phone}`}
        className="text-sm text-brand-700 hover:underline"
      >
        {dispatch.customer_phone}
      </a>
    </Section>
  ) : null;

  const notesEl = (
    <div className="space-y-3">
      {dispatch.notes && (
        <Section label="ESC notes (read-only)">
          <p className="whitespace-pre-wrap rounded-md bg-slate-50 p-3 text-sm text-slate-800">
            {dispatch.notes}
          </p>
        </Section>
      )}
      <Section label="Pegasus notes (internal)">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={variant === "strip" ? 3 : 4}
          placeholder="Add an internal note for this dispatch…"
          className="w-full resize-none rounded-md border border-hairline bg-white p-2 text-sm text-slate-800 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
        />
        <div className="mt-1.5 flex items-center gap-2">
          <Button
            variant="primary"
            size="sm"
            loading={saveNote.isPending}
            disabled={!dirty}
            onClick={() =>
              saveNote.mutate({
                dispatch: dispatch.dispatch,
                custno: dispatch.custno,
                locno: dispatch.locno,
                note: draft,
              })
            }
          >
            Save note
          </Button>
          {!dirty && savedNote && (
            <span className="text-xs text-slate-400">
              Saved{pegNote?.updated_by ? ` · ${pegNote.updated_by}` : ""}
            </span>
          )}
          {saveNote.isError && (
            <span className="text-xs text-red-600">Couldn&apos;t save</span>
          )}
        </div>
      </Section>
    </div>
  );

  // Estimate fields are stored in MINUTES (tech_est_hours is misnamed). Prefer
  // the per-tech estimate over the dispatch-level one when both exist.
  const estimateEl =
    dispatch.tech_est_hours || dispatch.dispatch_est_min ? (
      <Section label="Estimate">
        <div className="text-sm text-slate-700">
          {formatDuration(dispatch.tech_est_hours ?? dispatch.dispatch_est_min)}
          {dispatch.tech_est_hours &&
            dispatch.dispatch_est_min &&
            dispatch.tech_est_hours !== dispatch.dispatch_est_min && (
              <span className="ml-2 text-xs text-slate-500">
                (dispatch-level {formatDuration(dispatch.dispatch_est_min)})
              </span>
            )}
        </div>
      </Section>
    ) : null;

  const actionsEl = (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant="primary"
        onClick={() => {
          onClose();
          navigate(`/customers/${dispatch.custno}`);
        }}
      >
        <CustomerLink
          custno={dispatch.custno}
          locno={dispatch.locno}
          className="text-white hover:no-underline hover:text-white"
        >
          Qualify
        </CustomerLink>
      </Button>
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
    </div>
  );

  // Horizontal strip layout — fits the cockpit's bottom pane on one screen.
  if (variant === "strip") {
    return (
      <div className="grid h-full grid-cols-12 gap-x-6 gap-y-3">
        <div className="col-span-3 space-y-2">
          {headerEl}
          {pillsEl}
        </div>
        <div className="col-span-3 space-y-3">
          {addressEl}
          {techEl}
          {phoneEl}
        </div>
        <div className="col-span-4 flex min-h-0 flex-col">{notesEl}</div>
        <div className="col-span-2 flex flex-col justify-between gap-3">
          {estimateEl}
          {actionsEl}
        </div>
      </div>
    );
  }

  // Vertical stack — used inside the modal.
  return (
    <div className="space-y-4">
      {headerEl}
      {pillsEl}
      {addressEl}
      {techEl}
      {phoneEl}
      {notesEl}
      <div className="border-t border-slate-200 pt-4">
        {estimateEl}
        <div className={estimateEl ? "mt-4" : undefined}>{actionsEl}</div>
      </div>
    </div>
  );
}

function Section({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
      </div>
      {children}
    </div>
  );
}
